import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Gamepad, Sparkles, Mail, Phone, MessageSquare } from 'lucide-react';

const About = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [status, setStatus] = useState('');
  const contactRef = useRef(null);
  const supportRef = useRef(null);
  const topRef = useRef(null);
  const location = useLocation();
  const navbarHeight = 69; // Approximate height of navbar in pixels

  useEffect(() => {
    // Function to handle scrolling with offset for fixed navbar
    const scrollToElementWithOffset = (elementRef) => {
      if (elementRef.current) {
        const offsetTop = elementRef.current.offsetTop - navbarHeight;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    };

    // Check if we need to scroll to contact section
    if (location.state?.scrollToContact || location.hash === '#contact') {
      scrollToElementWithOffset(contactRef);
    }
    
    // Check if we need to scroll to support section
    if (location.state?.scrollToSupport || location.hash === '#support') {
      scrollToElementWithOffset(supportRef);
    }
    
    // Handle hash changes for smooth scrolling with offset
    const handleHashChange = () => {
      if (location.hash === '#contact') {
        scrollToElementWithOffset(contactRef);
      } else if (location.hash === '#support') {
        scrollToElementWithOffset(supportRef);
      } else if (location.hash === '') {
        // Handle empty hash (could be when clicking "About" in navbar)
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('Message sent successfully!');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setStatus('Failed to send message. Please try again.');
      }
    } catch (error) {
      setStatus('Error sending message.');
    }
  };

  return (
    <div ref={topRef} className="snap-y snap-mandatory overflow-y-scroll bg-white w-full pt-16"> {/* Added pt-16 for navbar spacing */}
      {/* Hero Section from Home.js */}
      <section id="hero" className="py-36 w-full snap-start relative overflow-hidden flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100 via-blue-50 to-white"></div>
        <div className="w-full relative z-10">
          <div className="flex items-center justify-center h-full">
            <div className="max-w-2xl">
              <div className="flex items-center gap-4 mb-6 justify-left">
                <Gamepad className="w-12 h-12 text-purple-600 " />
                <h2 className="text-6xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Stray Apps
                </h2>
              </div>
              <p className="text-2xl mb-8 text-gray-700 text-left">
              Your one-stop solution for innovative AI/ML solutions, digital transformation, app development and open-source developments.
                <Sparkles className="inline ml-2 w-6 h-6 text-yellow-500 animate-pulse" />
              </p>
              <div className="text-center">
                <Link to="/services" 
                  className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 px-8 py-4 rounded-lg text-xl font-bold text-white
                  transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/50">
                  Go to Apps
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rest of the component remains unchanged */}
      {/* About Section from Home.js */}
      <section className="py-20 w-full snap-start relative overflow-hidden flex items-center">
        {/* Content unchanged */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-purple-50 to-white"></div>
        <div className="w-full relative z-10">
        <h2 className="text-5xl font-bold mb-12 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent text-center ">
                Digital Solution Partner
        </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">

          {/* Left Side: Text & Cards */}
            <div className="max-w-4xl">
            <div className="p-4 flex flex-col space-y-8">
            <div className="bg-white p-4 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-purple-100">
                  <h3 className="text-2xl font-bold mb-4 text-purple-600">App development </h3>
                  <p className="text-lg text-gray-600">
                  Our in-house development team provides applied AI/ML, DevOps, DevSecOps, web app development, mobile app development, IoT services, and more.
                  </p>
                </div>
           
                <div className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-purple-100">
                  <h3 className="text-2xl font-bold mb-4 text-purple-600">Consultation</h3>
                  <p className="text-lg text-gray-600">
                  We offer analysis and consultation on digitalizing or upgrading existing digital workflows, setting goals, and creating an implementation roadmap.
                  </p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-purple-100">
                  <h3 className="text-2xl font-bold mb-4 text-purple-600">System ROI analysis</h3>
                  <p className="text-lg text-gray-600">
                  Our team will strategically help you optimize your digital expenses and manage your subscriptions through a one-stop service gateway.
                  </p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-purple-100">
                  <h3 className="text-2xl font-bold mb-4 text-purple-600">Integration Partner</h3>
                  <p className="text-lg text-gray-600">
                  We provide end-to-end liaising with multiple solution partners and manage the operation and maintenance of commissioned systems. 
                  </p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-purple-100">
                  <h3 className="text-2xl font-bold mb-4 text-purple-600">Opensource contributions</h3>
                  <p className="text-lg text-gray-600">
                  We are a passionate team of developers and solution engineers committed to contributing to and growing with the open-source community.
                  </p>
                </div>
              </div>
            
            
            </div>

          {/* Right Side: Image */}
            <div className="p-4 flex justify-center items-center min-h-screen">
            <video 
            autoPlay 
            loop 
            muted 
            playsInline 
            className="w-full h-auto rounded-lg shadow-lg"
            >
            <source src="/static/videos/tec.mp4" type="video/mp4" />
            Your browser does not support the video tag.
           </video>
           </div>

          </div>
        </div>
      </section>

      {/* Support Section */}
      <section 
      id="support"
      ref={supportRef}
      className="py-36 w-full snap-start relative overflow-hidden flex items-center"
    >
        {/* Content unchanged */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-purple-50 to-white"></div>
        <div className="w-full relative z-10">
          <div className="flex items-center justify-center h-full">
            <div className="max-w-4xl w-full">
              <h2 className="text-5xl font-bold mb-12 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent text-center">
                Join Our Community
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                <div className="bg-white p-6 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-purple-100">
                  <Mail className="w-8 h-8 text-purple-600 mb-4" />
                  <h3 className="text-xl font-bold mb-2 text-purple-600">Email Us</h3>
                  <p className="text-gray-600">contact@strayapps.org</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-purple-100">
                  <Phone className="w-8 h-8 text-purple-600 mb-4" />
                  <h3 className="text-xl font-bold mb-2 text-purple-600">Call Us</h3>
                  <p className="text-gray-600">+65 96590235</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-purple-100">
<MessageSquare className="w-8 h-8 text-purple-600 mb-4" />
<h3 className="text-xl font-bold mb-2 text-purple-600">Follow us</h3>
<a
  href="https://www.linkedin.com/company/106416712/admin/dashboard/"
  target="_blank"
  rel="noopener noreferrer"
  className="block text-gray-600 hover:text-purple-600 underline mb-2"
>
  LinkedIn
</a>
<a
  href="https://huggingface.co/StrayApps"
  target="_blank"
  rel="noopener noreferrer"
  className="block text-gray-600 hover:text-purple-600 underline"
>
  Hugging Face
</a>
</div>
              </div>
              
              <div className="text-center mt-8">
                <Link 
                  to="/terms" 
                  className="text-sm text-gray-500 hover:text-purple-600 transition-colors"
                >
                  Terms and Conditions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section 
        id="contact" 
        ref={contactRef}
        className="py-20 inset-0 bg-gradient-to-b from-white via-purple-50 to-white"
      >
        <h3 className="text-5xl font-bold mb-12 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6 text-center">Get in Touch</h3>
        <p className="max-w-2xl mx-auto text-lg mb-6 text-center">
          Have a question or need a consultation? Fill out the form below, and we'll get back to you.
        </p>

        <form onSubmit={handleSubmit} className="max-w-lg mx-auto bg-white text-gray-900 p-6 rounded-lg shadow-lg">
          <div className="mb-4">
            <label className="block text-left font-semibold">Your Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full p-2 border rounded" />
          </div>

          <div className="mb-4">
            <label className="block text-left font-semibold">Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full p-2 border rounded" />
          </div>

          <div className="mb-4">
            <label className="block text-left font-semibold">Company Name</label>
            <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full p-2 border rounded" />
          </div>

          <div className="mb-4">
            <label className="block text-left font-semibold">Message</label>
            <textarea name="message" value={formData.message} onChange={handleChange} required className="w-full p-2 border rounded h-24"></textarea>
          </div>

          <button type="submit" className="w-full bg-blue-600 text-white py-2 font-semibold rounded hover:bg-blue-700">
            Send Message
          </button>

          {status && <p className="mt-4 text-gray-800">{status}</p>}
        </form>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-gray-900 text-gray-300 text-center">
        <p>© 2025 Stray Apps. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default About;